import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchBrandRelatedVariants,
  addBrandRelatedVariant,
  removeBrandRelatedVariant,
  fetchModelRelatedVariants,
  addModelRelatedVariant,
  removeModelRelatedVariant,
  RelatedVariant,
} from '../../api/vehicleRelatedVariants';
import {
  fetchVehicleBrand,
  VehicleBrand,
} from '../../api/vehicleBrands';
import {
  fetchVehicleModel,
  VehicleModel,
} from '../../api/vehicleModels';
import {
  fetchProductVariants,
  ProductVariant,
} from '../../api/productVariants';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

type ContextType = 'brand' | 'model';

const resolveContext = (section?: string): ContextType | null => {
  if (section === 'brands') {
    return 'brand';
  }
  if (section === 'models') {
    return 'model';
  }
  return null;
};

const VehicleRelatedProductsPage: FC = () => {
  const navigate = useNavigate();
  const { section, id } = useParams<{ section?: string; id?: string }>();
  const context = resolveContext(section);
  const [entity, setEntity] = useState<VehicleBrand | VehicleModel | null>(null);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [related, setRelated] = useState<RelatedVariant[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ProductVariant[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([]);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!context || !id) {
      return;
    }

    setEntity(null);
    setEntityError(null);

    const load = async () => {
      try {
        if (context === 'brand') {
          const brand = await fetchVehicleBrand(id);
          setEntity(brand);
        } else {
          const model = await fetchVehicleModel(id);
          setEntity(model);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load details.';
        setEntityError(message);
      }
    };

    load();
  }, [context, id]);

  useEffect(() => {
    if (!context || !id) {
      return;
    }

    setRelatedLoading(true);
    setRelatedError(null);

    const load = async () => {
      try {
        const result =
          context === 'brand'
            ? await fetchBrandRelatedVariants(id)
            : await fetchModelRelatedVariants(id);
        setRelated(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load related products.';
        setRelatedError(message);
      } finally {
        setRelatedLoading(false);
      }
    };

    load();
  }, [context, id, refreshCounter]);

  const handleSearch = async () => {
    if (!search.trim()) {
      setSearchResults([]);
      setSearchError('Enter a search term.');
      return;
    }

    setSearchError(null);
    setSearchLoading(true);

    try {
      const response = await fetchProductVariants({ search: search.trim(), limit: 20, page: 1 });
      setSearchResults(response.data);
      setSelectedVariantIds([]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to search variants.';
      setSearchError(message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBulkAdd = async () => {
    if (!context || !id || selectedVariantIds.length === 0) {
      return;
    }

    setLinking(true);
    setSearchError(null);

    try {
      const uniqueIds = Array.from(new Set(selectedVariantIds));
      for (const variantId of uniqueIds) {
        if (existingVariantIds.has(variantId)) {
          continue;
        }
        if (context === 'brand') {
          await addBrandRelatedVariant(id, variantId);
        } else {
          await addModelRelatedVariant(id, variantId);
        }
      }

      setSelectedVariantIds([]);
      setSearchResults([]);
      setSearch('');
      setRefreshCounter((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to add related products.';
      setSearchError(message);
    } finally {
      setLinking(false);
    }
  };

  const handleRemove = async (relationId: string) => {
    if (!context || !id) {
      return;
    }

    if (!window.confirm('Remove this related product?')) {
      return;
    }

    try {
      if (context === 'brand') {
        await removeBrandRelatedVariant(id, relationId);
      } else {
        await removeModelRelatedVariant(id, relationId);
      }
      setRefreshCounter((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to remove related product.';
      setRelatedError(message);
    }
  };

  const headerTitle = useMemo(() => {
    if (!entity) {
      return context === 'brand' ? 'Brand related products' : 'Model related products';
    }

    const label = context === 'brand' ? 'Brand' : 'Model';
    return `${label} – ${entity.name}`;
  }, [context, entity]);

  const existingVariantIds = useMemo(
    () => new Set(related.map((item) => item.productVariant.id)),
    [related],
  );

  const filteredSearchResults = useMemo(() => searchResults, [searchResults]);
  const filteredRelated = useMemo(() => related, [related]);

  const toggleVariantSelection = (variantId: string) => {
    setSelectedVariantIds((prev) => {
      if (prev.includes(variantId)) {
        return prev.filter((id) => id !== variantId);
      }
      return [...prev, variantId];
    });
  };

  if (!context || !id) {
    return (
      <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
        <p className="text-sm text-danger">Invalid related products route.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl  bg-white p-6 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">


      <PageHeaderWithBack
        title={headerTitle}
        description={<p className="mt-2 max-w-2xl text-sm text-slate-500">
          Link frequently recommended product variants so they appear instantly when configuring job offers for this {context === 'brand' ? 'brand' : 'model'}.
        </p>}
        onBack={() => navigate(context === 'brand' ? '/vehicle-management/brands' : '/vehicle-management/models')}
        backLabel={`Back to ${context === 'brand' ? 'brands' : 'models'}`}
      />



      {entityError ? <p className="mt-4 text-sm text-danger">{entityError}</p> : null}

      <section className="mt-6 space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-inner">
          <h2 className="text-sm font-semibold text-slate-700">Add related product variant</h2>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product variants..."
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
              disabled={searchLoading}
            />
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              {searchLoading ? 'Searching…' : 'Search'}
            </button>
          </div>
          {searchError ? <p className="mt-2 text-sm text-danger">{searchError}</p> : null}

          {searchResults.length > 0 ? (
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-center">Select</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">ERP ID</th>
                    <th className="px-4 py-3 font-semibold">Article</th>
                    <th className="px-4 py-3 font-semibold">Brand</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSearchResults.map((variant) => {
                    const disabled = existingVariantIds.has(variant.id);
                    const checked = selectedVariantIds.includes(variant.id);
                    return (
                      <tr key={variant.id} className="even:bg-slate-50/60">
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                            checked={checked || disabled}
                            onChange={() => toggleVariantSelection(variant.id)}
                            disabled={disabled || linking}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-700">{variant.name}</td>
                        <td className="px-4 py-3 text-slate-500">{variant.erpId}</td>
                        <td className="px-4 py-3 text-slate-500">{variant.articleNumber ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-500">{variant.brand?.name ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
                <span className="text-xs text-slate-500">
                  {selectedVariantIds.length} variant{selectedVariantIds.length === 1 ? '' : 's'} selected
                </span>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl ml-2 bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
                  onClick={handleBulkAdd}
                  disabled={linking || selectedVariantIds.length === 0}
                >
                  {linking ? 'Linking…' : 'Link selected'}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-inner">
          <h2 className="text-sm font-semibold text-slate-700">Linked variants</h2>
          {relatedError ? (
            <p className="mt-2 text-sm text-danger">{relatedError}</p>
          ) : null}
          {relatedLoading ? (
            <p className="mt-4 text-sm text-slate-400">Loading related products…</p>
          ) : filteredRelated.length === 0 ? (
            <p className="mt-4 text-sm text-slate-400">No related variants linked yet.</p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">ERP ID</th>
                    <th className="px-4 py-3 font-semibold">Article</th>
                    <th className="px-4 py-3 font-semibold">Brand</th>
                    <th className="px-4 py-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRelated.map((relation) => {
                    const variant = relation.productVariant;
                    return (
                      <tr key={relation.id} className="even:bg-slate-50/60">
                        <td className="px-4 py-3 font-medium text-slate-700">{variant.name}</td>
                        <td className="px-4 py-3 text-slate-500">{variant.erpId}</td>
                        <td className="px-4 py-3 text-slate-500">{variant.articleNumber ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-500">{variant.brand?.name ?? '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-danger/40 px-3 py-1.5 text-xs font-semibold text-danger transition hover:bg-danger/10"
                            onClick={() => handleRemove(relation.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VehicleRelatedProductsPage;
